import { memo, useEffect, useRef, useState } from "react";
import {
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  GiftIcon,
  HeartIcon,
  Link,
  PaintBrushIcon,
  PlusIcon,
  RectangleStackIcon,
  UserGroupIcon,
  useUserPermissions,
} from "@";

/**
 * Mega-menu triggered by the navbar "+ إضافة" button.
 * Groups quick-add destinations by domain. Items are permission-gated;
 * sections with zero visible items are hidden entirely.
 */

const SECTIONS = [
  {
    key: "donations",
    title: "التبرعات والإهداءات",
    icon: HeartIcon,
    items: [
      {
        name: "فرصة تبرع",
        description: "أضف مشروع تبرع جديد للمتجر.",
        to: "/projects/create",
        permission: "editProjects",
      },
      {
        name: "إهداء جديد",
        description: "أنشئ مشروع إهداء قابل للمشاركة.",
        to: "/giftProjects/create",
        permission: "editGiftProject",
        icon: GiftIcon,
      },
    ],
  },

  {
    key: "website",
    title: "الموقع الإلكتروني",
    icon: RectangleStackIcon,
    items: [
      {
        name: "صفحة هبوط",
        description: "صفحة تسويقية مستقلة لحملة محددة.",
        to: "/website/landing-pages/create",
        permission: "editSitePages",
      },
      {
        name: "ثيم جديد",
        description: "أنشئ ثيمًا مخصصًا لمتجرك.",
        to: "/website/themes/create",
        permission: "editThemes,publishThemes",
        icon: PaintBrushIcon,
      },
    ],
  },
  {
    key: "team",
    title: "الفريق",
    icon: UserGroupIcon,
    items: [
      {
        name: "موظف جديد",
        description: "أضف عضوًا للفريق مع تحديد الصلاحيات.",
        to: "/settings-users/create",
        permission: "permissions",
      },
    ],
  },
  {
    key: "messages",
    title: "المراسلات",
    icon: ChatBubbleLeftRightIcon,
    items: [
      {
        name: "رسالة جماعية",
        description: "أرسل رسالة لمجموعة من المتبرعين.",
        to: "/send-multiple",
        permission: "sendMessage",
      },
      {
        name: "رسالة مخصصة",
        description: "أرسل رسالة لرقم أو متبرع محدد.",
        to: "/send-single",
        permission: "sendMessage",
      },
      {
        name: "رسالة مؤتمتة",
        description: "أتمتة الرسائل وفق محفّز معيّن.",
        to: "/automated/create",
        permission: "sendMessage",
      },
      {
        name: "حملة WhatsApp",
        description: "حملة WhatsApp جماعية موجَّهة.",
        to: "/whatsapp-campaign/create",
        permission: "whatsappCampaign",
      },
    ],
  },
];

const anyOf = (permissionMap, perm) =>
  perm
    .split(",")
    .map((p) => p.trim())
    .some((p) => permissionMap?.[p]);

const AddMenu = () => {
  const { permissions } = useUserPermissions();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Visible sections / items based on permissions
  const visibleSections = SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => anyOf(permissions, item.permission)),
  })).filter((section) => section.items.length > 0);

  if (visibleSections.length === 0) return null;

  return (
    <div ref={containerRef} className="">
      {/* Trigger */}
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={[
          "group relative inline-flex h-10 items-center  overflow-hidden rounded-full px-1.5 text-sm font-semibold transition-all duration-200 gap-1 sm:ps-2.5 sm:pe-3.5",
          open
            ? "bg-purple-50 text-purple-900 ring-1 ring-purple-200 dark:bg-purple-900/50 dark:text-purple-300 dark:ring-purple-700/50"
            : "bg-white/50 dark:bg-gray-900/50 text-gray-500 ring-1 ring-gray-200 dark:ring-gray-700 dark:text-gray-300",
        ].join(" ")}
      >
        <span
          className={[
            "flex h-7 w-7 dark:text-gray-300 items-center justify-center rounded-full transition-colors",
            open
              ? "   "
              : "bg-white/50 dark:bg-gray-900/50 text-gray-500 group-hover:bg-white/25 dark:group-hover:bg-gray-900/25",
          ].join(" ")}
        >
          <PlusIcon className="h-4 w-4" strokeWidth={2.5} />
        </span>
        <span className="hidden sm:inline">إضافة</span>
        <ChevronDownIcon
          className={`hidden h-3 w-3 transition-transform duration-200 sm:inline ${
            open ? "rotate-180" : ""
          }`}
          strokeWidth={2.5}
        />
      </button>

      {/* Mega menu */}
      {open && (
        <div
          role="menu"
          aria-label="إضافة جديد"
          className="absolute end-0 z-[60] mt-2 w-80 sm:w-[min(96vw,640px)] origin-center overflow-hidden rounded-2xl border border-purple-100 bg-white shadow-2xl shadow-purple-900/15 ring-1 ring-purple-100/40 [animation:fadeIn_180ms_ease-out_both] dark:border-gray-700/60 dark:bg-gray-900 dark:shadow-black/40 dark:ring-purple-900/40"
        >
          {/* Header band */}
          <div className="relative overflow-hidden border-b border-gray-100 bg-gradient-to-bl from-purple-50 via-white to-white px-5 py-3 dark:border-gray-700/60 dark:from-purple-950/40 dark:via-gray-900 dark:to-gray-900">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-purple-700 dark:text-purple-300">
              اختصارات الإنشاء
            </p>
            <h3 className="mt-0.5 text-base font-bold tracking-tight text-gray-900 dark:text-gray-100">
              ماذا تريد أن تضيف اليوم؟
            </h3>
          </div>

          {/* Sections grid */}
          <div className="grid max-h-[70vh] gap-x-5 gap-y-3 overflow-y-auto p-4 sm:grid-cols-2">
            {visibleSections.map((section) => {
              const SectionIcon = section.icon;
              return (
                <div key={section.key}>
                  {/* Section header */}
                  <div className=" flex items-center gap-2 px-2">
                    <span
                      aria-hidden
                      className="flex h-6 w-6 items-center justify-center rounded-md bg-purple-50 text-purple-900 ring-1 ring-purple-100 dark:bg-purple-950/40 dark:text-purple-300 dark:ring-purple-800/40"
                    >
                      <SectionIcon className="h-3.5 w-3.5" strokeWidth={2} />
                    </span>
                    <h4 className="text-[11px] font-bold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-100">
                      {section.title}
                    </h4>
                  </div>

                  {/* Items */}
                  <ul className="space-y-0.5" role="none">
                    {section.items.map((item) => {
                      const ItemIcon = item.icon;
                      return (
                        <li key={item.to} role="none">
                          <Link
                            to={item.to}
                            role="menuitem"
                            onClick={() => setOpen(false)}
                            className="group/item flex items-start gap-2.5 rounded-2xl px-2 pt-2 transition-colors hover:bg-purple-50/70 focus:bg-purple-50/70 focus:outline-none dark:hover:bg-purple-950/30 dark:focus:bg-purple-950/30"
                          >
                            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gray-50 text-gray-500 ring-1 ring-gray-100 transition-all group-hover/item:bg-purple-100 group-hover/item:text-purple-900 group-hover/item:ring-purple-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700 dark:group-hover/item:bg-purple-900/40 dark:group-hover/item:text-purple-300">
                              {ItemIcon ? (
                                <ItemIcon
                                  className="h-3.5 w-3.5"
                                  strokeWidth={2}
                                />
                              ) : (
                                <PlusIcon
                                  className="h-3.5 w-3.5"
                                  strokeWidth={2.5}
                                />
                              )}
                            </span>
                            <span className="flex-1">
                              <span className="block text-[13px] font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                                {item.name}
                              </span>
                              <span className="mt-0.5 block text-[11px] leading-snug text-gray-500 dark:text-gray-400">
                                {item.description}
                              </span>
                            </span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(AddMenu);
